<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
  import {
    computed,
    ref,
    onBeforeMount,
    watch,
    nextTick,
    onUnmounted,
  } from 'vue';
  import {createFocusTrap, type FocusTrap} from 'focus-trap';
  import {useI18n} from 'vue-i18n';
  import {getComponentConfig} from '@core/utils/getComponentConfig';
  import Icon from '@core/components/Icon.vue';
  import {
    Card,
    CardTitle,
    CardContent,
    CardActions,
  } from '@core/components/Card';

  interface Props {
    modelValue: boolean;
    maxWidth?: string | number;
    persistent?: boolean;
    display?: 'card' | 'blank';
    title?: string;
    showClose?: boolean;
    loading?: boolean;
    cardProps?: Record<string, unknown>;
    cardClass?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    maxWidth: '500px',
    persistent: false,
    display: 'card',
    title: '',
    showClose: true,
    loading: false,
    cardProps: () => ({}),
    cardClass: '',
  });

  const emit = defineEmits(['update:modelValue', 'close', 'keydown']);

  const {t} = useI18n();

  const defaultConfig = {
    base: 'fixed inset-0 z-50 flex items-center justify-center p-4',
    overlay:
      'fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm transition-opacity',
    card: {
      variant: 'elevated' as const,
      rounded: 'xl' as const,
      base: undefined as string | undefined,
    },
    closeIcon: 'X',
  };

  const config = ref(defaultConfig);

  onBeforeMount(async () => {
    config.value = await getComponentConfig('Modal', defaultConfig);
  });

  const isOpen = computed({
    get: () => props.modelValue,
    set: val => emit('update:modelValue', val),
  });

  const closeIconName = computed(() => {
    const icon = config.value.closeIcon;
    if (!icon) return 'x';
    return icon.toLowerCase();
  });

  const close = () => {
    if (!props.persistent && !props.loading) {
      isOpen.value = false;
      emit('close');
    }
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && !props.persistent) {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  };

  const dialogRef = ref<HTMLElement | null>(null);
  let isListenerAttached = false;
  let focusTrap: FocusTrap | null = null;

  function deactivateFocusTrap() {
    if (focusTrap) {
      focusTrap.deactivate();
      focusTrap = null;
    }
  }

  function attachKeydownListener() {
    if (isListenerAttached) return;
    document.addEventListener('keydown', handleKeydown);
    isListenerAttached = true;
  }

  function detachKeydownListener() {
    if (!isListenerAttached) return;
    document.removeEventListener('keydown', handleKeydown);
    isListenerAttached = false;
  }

  function activateFocusTrap(
    el: HTMLElement,
    returnFocusTarget: HTMLElement | null,
  ) {
    const canReturnTo =
      returnFocusTarget &&
      document.contains(returnFocusTarget) &&
      !el.contains(returnFocusTarget)
        ? returnFocusTarget
        : null;

    focusTrap = createFocusTrap(el, {
      escapeDeactivates: false,
      allowOutsideClick: true,
      returnFocusOnDeactivate: true,
      fallbackFocus: el,
      setReturnFocus: canReturnTo ? () => canReturnTo : undefined,
    });
    focusTrap.activate();
  }

  async function onModalOpen() {
    const active = document.activeElement;
    const returnFocusTarget = active instanceof HTMLElement ? active : null;

    await nextTick();
    await nextTick();
    deactivateFocusTrap();
    const el = dialogRef.value;
    if (el) activateFocusTrap(el, returnFocusTarget);
    attachKeydownListener();
  }

  function onModalClose() {
    deactivateFocusTrap();
    detachKeydownListener();
  }

  // Focus trap (Tab containment) + return focus on close; ESC at document level
  watch(isOpen, newValue => (newValue ? onModalOpen() : onModalClose()), {
    immediate: true,
  });

  onUnmounted(() => {
    deactivateFocusTrap();
    detachKeydownListener();
  });
</script>

<template>
  <Teleport to="body">
    <!-- Backdrop: enter is instant (duration-0); leave fades. -->
    <Transition
      enter-active-class="transition-opacity duration-0"
      enter-from-class="opacity-100"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0">
      <div
        v-if="isOpen"
        :class="['modal', config.base]"
        v-bind="$attrs">
        <!-- Overlay: mouse dismiss; Escape handled on document -->
        <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions, vuejs-accessibility/click-events-have-key-events -->
        <div
          :class="['modal__overlay', config.overlay]"
          @click="close" />
        <!-- Dialog Content: enter is intentionally instant (duration-0); only leave animates. -->
        <Transition
          enter-active-class="transition-opacity duration-0"
          enter-from-class="opacity-100 scale-100"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition-opacity duration-200"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95">
          <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -- @click.stop prevents overlay close when interacting with dialog surface -->
          <dialog
            v-if="isOpen"
            ref="dialogRef"
            open
            class="modal__dialog relative z-[100] m-0 w-full border-0
              bg-transparent p-0 text-inherit outline-none focus:outline-none"
            tabindex="-1"
            aria-modal="true"
            :aria-labelledby="title ? 'modal-title' : undefined"
            :style="{
              maxWidth:
                typeof maxWidth === 'number'
                  ? `${maxWidth}px`
                  : maxWidth || '500px',
            }"
            @click.stop
            @keydown="emit('keydown', $event)">
            <template v-if="display === 'card'">
              <Card
                v-bind="{
                  variant: config.card.variant,
                  rounded: config.card.rounded,
                  ...cardProps,
                }"
                :class="
                  [
                    config.card?.base,
                    cardClass,
                    'flex max-h-[70vh] min-h-0 flex-col overflow-hidden',
                  ]
                    .filter(Boolean)
                    .join(' ')
                "
                :loading="loading">
                <CardTitle
                  v-if="title || showClose || $slots.title"
                  class="flex-shrink-0">
                  <div class="flex items-center justify-between w-full">
                    <slot name="title">
                      <span :id="title ? 'modal-title' : undefined">{{
                        title
                      }}</span>
                    </slot>
                    <button
                      v-if="showClose"
                      type="button"
                      class="modal__close -me-2 inline-flex shrink-0
                        cursor-pointer items-center justify-center rounded-md
                        p-2 text-gray-500 transition-colors hover:text-gray-700
                        focus-visible:outline-none focus-visible:ring-2
                        focus-visible:ring-brand-primary
                        focus-visible:ring-offset-2 dark:text-gray-400
                        dark:hover:text-gray-200
                        dark:focus-visible:ring-offset-zinc-900"
                      :aria-label="t('core.closeMsg')"
                      @click.stop="close">
                      <Icon
                        :name="closeIconName"
                        class="pointer-events-none text-current"
                        :size="20" />
                    </button>
                  </div>
                </CardTitle>
                <div
                  class="min-h-0 flex-auto overflow-y-auto overflow-x-hidden
                    overscroll-contain scrollbar-hide">
                  <CardContent>
                    <slot />
                  </CardContent>
                </div>
                <CardActions
                  v-if="$slots.actions"
                  class="flex-shrink-0">
                  <slot name="actions" />
                </CardActions>
              </Card>
            </template>
            <template v-else-if="display === 'blank'">
              <slot />
            </template>
          </dialog>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
